/**
 * [Gurumi 에디터]
 * 역할: G5GurumiEditor — webeditor oEditors 호환 + yesmoney TipTap UI
 * 작성/수정일: 2026-07-29
 */
import { Editor, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle as TiptapTextStyle, Color, BackgroundColor } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions/placeholder';

/**
 * [강조 색 클래스]
 * - 역할: background-color 적용 시 g5-gurumi-hl 클래스를 붙여 CSS로 전체 높이 커버
 * - 작성/수정일: 2026-08-12
 */
const TextStyle = TiptapTextStyle.extend({
  renderHTML({ HTMLAttributes }) {
    const style = typeof HTMLAttributes.style === 'string' ? HTMLAttributes.style : '';
    const hasBg =
      !!HTMLAttributes.backgroundColor ||
      /background-color\s*:/i.test(style);
    const className = [HTMLAttributes.class, hasBg ? 'g5-gurumi-hl' : null]
      .filter(Boolean)
      .join(' ');
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, className ? { class: className } : {}),
      0,
    ];
  },
});

import { StyledBulletList } from './extensions/list-styles.js';
import { FontVariant } from './extensions/font-variant.js';
import { ResizableImage } from './extensions/resizable-image.js';
import { EditorYoutube } from './extensions/youtube.js';
import { EditorLinkPreview } from './extensions/link-preview.js';
import { uploadImage, fetchLinkPreview } from './api.js';
import { createToolbar } from './ui/toolbar.js';
import { openInputModal } from './ui/input-modal.js';
import { openLinkPreviewModal } from './ui/link-modal.js';
import { getActiveColors } from './color-palette.js';
import { resolveGurumiFeatures, readGurumiFeaturesFromElement } from './features.js';
import {
  computeEditorImageDisplaySize,
  findEditorImagePosBySrc,
  loadImageFileDimensions,
  replaceEditorImageAtPos,
} from './image-upload.js';
import {
  editorHasExternalImages,
  normalizePastedHtmlEmojis,
  rehostExternalImagesInEditor,
} from './image-rehost.js';
import { extractAffiliatePreviewFromPaste } from './affiliate-link.js';
import { getBoardImageMaxWidth, getEditorMediaMaxWidth, isEmptyHtml, isHttpUrl, isYoutubeUrl } from './utils.js';
import './g5-gurumi.css';

/**
 * [Gurumi 툴바 프리셋]
 * - 역할: features 플래그에 맞춰 TipTap 확장 구성
 * - 작성/수정일: 2026-08-12
 *
 * @param {string} mode
 * @param {import('./features.js').GurumiFeatureFlags} features
 */
function buildExtensions(mode, features) {
  const isComment = mode === 'comment';
  const extensions = [
    StarterKit.configure({
      link: false,
      bulletList: false,
      heading: features.heading ? { levels: [1, 2, 3] } : false,
      blockquote: !!features.blockquote,
      codeBlock: !!features.codeBlock,
      horizontalRule: !!features.horizontalRule,
      orderedList: !!features.orderedList,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      defaultProtocol: 'https',
      HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
    }),
  ];

  // [플레이스홀더] 역할: 간소화 댓글 안내 문구 — 작성/수정일: 2026-07-29
  if (isComment) {
    extensions.push(
      Placeholder.configure({
        placeholder: '@함께 대화에 참여해보세요',
        emptyEditorClass: 'is-editor-empty',
      }),
    );
  }

  if (features.bulletList && !isComment) {
    extensions.push(StyledBulletList);
  }

  // [글자색·형광펜] TextStyle 기반 — 작성/수정일: 2026-08-12
  if (features.color) {
    extensions.push(TextStyle, Color, BackgroundColor);
  }

  // [가가 텍스트 스타일] 선택 구간 인라인 글자 크기 — 작성/수정일: 2026-07-29
  if (features.fontVariant) {
    extensions.push(FontVariant);
  }

  if (features.align) {
    extensions.push(TextAlign.configure({ types: ['heading', 'paragraph'] }));
  }

  if (features.image) {
    extensions.push(ResizableImage);
  }

  if (features.youtube) {
    extensions.push(EditorYoutube);
  }

  if (features.linkPreview) {
    extensions.push(EditorLinkPreview);
  }

  return extensions;
}

function getEditorState(editor) {
  if (!editor) return {};
  return {
    canUndo: editor.can().undo(),
    canRedo: editor.can().redo(),
    bold: editor.isActive('bold'),
    italic: editor.isActive('italic'),
    link: editor.isActive('link'),
    image: editor.isActive('image'),
    left:
      editor.isActive({ textAlign: 'left' }) ||
      editor.getAttributes('image').alignment === 'left' ||
      editor.getAttributes('youtube').alignment === 'left' ||
      editor.getAttributes('linkPreview').alignment === 'left',
    center:
      editor.isActive({ textAlign: 'center' }) ||
      editor.getAttributes('image').alignment === 'center' ||
      editor.getAttributes('youtube').alignment === 'center' ||
      editor.getAttributes('linkPreview').alignment === 'center',
    right:
      editor.isActive({ textAlign: 'right' }) ||
      editor.getAttributes('image').alignment === 'right' ||
      editor.getAttributes('youtube').alignment === 'right' ||
      editor.getAttributes('linkPreview').alignment === 'right',
    // [글자색·형광펜] 툴바 표시용 — 작성/수정일: 2026-08-12
    textColor: getActiveColors(editor).text,
    highlightColor: getActiveColors(editor).highlight,
  };
}

/**
 * [Gurumi 에디터]
 * 역할: webeditor G5WebEditor 와 동일하게 oEditors에 등록되는 인스턴스
 * 작성/수정일: 2026-07-29
 */
function G5GurumiEditor(fieldId, options) {
  this.id = fieldId;
  this.options = options || {};
  this.mode = this.options.mode || 'write';
  this.elPlaceHolder = document.getElementById(fieldId);

  if (!this.elPlaceHolder) {
    throw new Error('G5GurumiEditor: textarea not found - ' + fieldId);
  }

  // [툴바 프리셋] textarea data-attr + options 병합 — 작성/수정일: 2026-08-12
  const fromEl = readGurumiFeaturesFromElement(this.elPlaceHolder);
  const resolved = resolveGurumiFeatures({
    mode: this.mode,
    preset: this.options.preset || fromEl.preset,
    features: Object.assign({}, fromEl.features || {}, this.options.features || {}),
  });
  this.preset = resolved.preset;
  this.features = resolved.features;

  this._editor = null;
  this._wrap = null;
  this._toolbarApi = null;
  this._rehosting = false;
  this._build();
}

G5GurumiEditor.prototype._build = function () {
  const self = this;
  const ta = this.elPlaceHolder;
  const mode = this.mode;
  const features = this.features;
  const config = {
    editorUrl: this.options.editorUrl || '',
    pluginUrl: this.options.editorUrl || '',
    uploadNonce: this.options.uploadNonce || '',
    nonce: this.options.uploadNonce || '',
    boTable: this.options.boTable || '',
    uploadMode: this.options.uploadMode || 'board',
    maxUploadBytes: this.options.maxUploadBytes || 10 * 1024 * 1024,
    // [이미지 폭] 게시판 bo_image_width — 작성/수정일: 2026-08-09
    imageMaxWidth: Number(this.options.imageMaxWidth || 0) || getBoardImageMaxWidth(0),
    endpoints: this.options.endpoints || {},
  };
  // config.js / createCommentEditor 옵션을 전역 opts에 동기화 (리사이즈 clamp 공통 사용)
  if (typeof window !== 'undefined') {
    window.g5_gurumi_opts = Object.assign({}, window.g5_gurumi_opts || {}, {
      imageMaxWidth: config.imageMaxWidth || 0,
    });
  }

  const wrap = document.createElement('div');
  wrap.className = 'g5-gurumi-wrap editor-container';
  wrap.dataset.field = this.id;
  wrap.dataset.mode = mode;
  wrap.dataset.preset = this.preset || 'write';
  ta.parentNode.insertBefore(wrap, ta);
  ta.style.display = 'none';
  ta.classList.add('g5-gurumi-source');
  this._wrap = wrap;

  const container = document.createElement('div');
  container.className = 'g5-gurumi-editor-shell';
  wrap.appendChild(container);

  const errorEl = document.createElement('p');
  errorEl.className = 'g5-gurumi-error';
  errorEl.hidden = true;
  errorEl.setAttribute('role', 'alert');

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/jpeg,image/png,image/gif,image/webp';
  fileInput.multiple = true;
  fileInput.className = 'g5-gurumi-file-input';
  fileInput.tabIndex = -1;
  wrap.appendChild(fileInput);

  const editorEl = document.createElement('div');
  editorEl.className =
    mode === 'comment'
      ? 'g5-gurumi-editor gurumi comment-rich-editor'
      : 'g5-gurumi-editor gurumi editor-content prose';

  function setError(msg) {
    if (msg) {
      errorEl.textContent = msg;
      errorEl.hidden = false;
    } else {
      errorEl.hidden = true;
    }
  }

  function setUploading(on) {
    self._toolbarApi?.setUploading(on);
  }

  function refreshToolbar() {
    self._toolbarApi?.updateToolbarState(getEditorState(self._editor));
  }

  const initialHtml = (ta.value || '').trim() || '<p></p>';

  let editor;
  try {
    editor = new Editor({
      element: editorEl,
      extensions: buildExtensions(mode, features),
      content: initialHtml,
      editorProps: {
        attributes: {
          // [WYSIWYG] 입력면에도 공개 본문 클래스(g5-gurumi-view) — 타이포 CSS 단일 소스 — 작성/수정일: 2026-08-12
          class:
            mode === 'comment'
              ? 'comment-rich-editor gurumi g5-gurumi-view'
              : 'editor-content prose max-w-none outline-none g5-gurumi-view',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': mode === 'comment' ? '댓글' : '내용',
        },
        // [외부 이미지 rehost] 이모지 img → 문자 치환 후 HTML 붙여넣기 — 작성/수정일: 2026-07-29
        transformPastedHTML(html) {
          return normalizePastedHtmlEmojis(html);
        },
        handlePaste(_view, event) {
          if (mode === 'comment') return false;
          if (features.image) {
            const files = Array.from(event.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
            if (files.length) {
              void handleImages(files);
              return true;
            }
          }
          const plain = event.clipboardData?.getData('text/plain')?.trim() ?? '';
          const html = event.clipboardData?.getData('text/html') ?? '';

          // [Gurumi 제휴·OG] 역할: 쿠팡 배너/iframe · 제휴 URL → 카드 (iframe 본문 저장 금지)
          // 작성/수정일: 2026-07-29
          if (features.linkPreview) {
            const affiliate = extractAffiliatePreviewFromPaste(html, plain);
            if (affiliate) {
              event.preventDefault();
              if (affiliate.kind === 'card') {
                void insertLinkPreview(affiliate.card);
              } else {
                void insertLinkPreview(affiliate.url);
              }
              return true;
            }

            if (plain && isHttpUrl(plain) && !isYoutubeUrl(plain) && editor.state.selection.empty && !html) {
              event.preventDefault();
              void insertLinkPreview(plain);
              return true;
            }
          }
          // 외부 img는 붙여넣기 시 URL 유지 → 작성완료(submit) 시 rehost
          return false;
        },
        handleDrop(_view, event) {
          if (mode === 'comment' || !features.image) return false;
          const files = Array.from(event.dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'));
          if (!files.length) return false;
          event.preventDefault();
          void handleImages(files);
          return true;
        },
      },
      onUpdate: ({ editor: ed }) => {
        self.syncToTextarea();
        refreshToolbar();
      },
      onSelectionUpdate: () => refreshToolbar(),
      onTransaction: () => refreshToolbar(),
    });
  } catch (err) {
    // [글수정 에디터] 역할: 본문 파싱 실패 시 빈 문서로 재시도 — 작성/수정일: 2026-07-29
    console.error('[g5-gurumi] editor init failed, fallback empty content', err);
    editor = new Editor({
      element: editorEl,
      extensions: buildExtensions(mode, features),
      content: '<p></p>',
      editorProps: {
        attributes: {
          class:
            mode === 'comment'
              ? 'comment-rich-editor gurumi g5-gurumi-view'
              : 'editor-content prose max-w-none outline-none g5-gurumi-view',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': mode === 'comment' ? '댓글' : '내용',
        },
      },
      onUpdate: () => {
        self.syncToTextarea();
        refreshToolbar();
      },
      onSelectionUpdate: () => refreshToolbar(),
      onTransaction: () => refreshToolbar(),
    });
    try {
      editor.commands.setContent(initialHtml, { emitUpdate: false });
    } catch (err2) {
      console.error('[g5-gurumi] setContent failed', err2);
      setError('기존 본문을 일부 불러오지 못했습니다. 내용을 확인해 주세요.');
    }
  }

  this._editor = editor;

  /**
   * [외부 이미지 rehost]
   * 역할: 작성완료/수정 제출 직전 외부 img만 import · 실패 시 제출 중단
   * 작성/수정일: 2026-07-29
   */
  async function rehostExternalImagesBeforeSubmit() {
    if (self._rehosting) {
      return { ok: false, skipped: true };
    }
    if (!editorHasExternalImages(editor)) {
      return { ok: true, replaced: 0, failed: 0 };
    }

    self._rehosting = true;
    setUploading(true);
    setError(null);

    try {
      const result = await rehostExternalImagesInEditor(editor, config);
      self.syncToTextarea();
      if (result.failed > 0) {
        setError(
          result.replaced === 0
            ? '외부 이미지를 가져오지 못했습니다. 링크가 차단되었거나 접근할 수 없습니다.'
            : `일부 이미지(${result.failed}개)를 가져오지 못했습니다. 다시 시도해 주세요.`,
        );
        return { ok: false, ...result };
      }
      return { ok: true, ...result };
    } catch (err) {
      setError(err.message || '외부 이미지를 가져오지 못했습니다.');
      return { ok: false, replaced: 0, failed: 1 };
    } finally {
      self._rehosting = false;
      setUploading(false);
      self.syncToTextarea();
    }
  }

  /**
   * [외부 이미지 rehost]
   * 역할: #fwrite 등 폼 submit을 가로채 재호스팅 후 검증·전송을 다시 진행
   * 작성/수정일: 2026-07-29
   */
  function bindFormSubmitRehost() {
    if (mode === 'comment') return;
    const form = ta.closest('form');
    if (!form || form.dataset.g5GurumiRehostBound === '1') return;
    form.dataset.g5GurumiRehostBound = '1';

    form.addEventListener(
      'submit',
      (event) => {
        if (!form.contains(ta)) return;
        if (form.dataset.g5GurumiRehostReady === '1') {
          delete form.dataset.g5GurumiRehostReady;
          return;
        }
        if (!editorHasExternalImages(editor)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const submitBtn = form.querySelector('#btn_submit, button[type="submit"], input[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        void (async () => {
          const result = await rehostExternalImagesBeforeSubmit();
          if (!result.ok) {
            if (submitBtn) submitBtn.disabled = false;
            return;
          }
          // requestSubmit(disabled submitter) 는 InvalidStateError → 재활성화 후 제출
          if (submitBtn) submitBtn.disabled = false;
          form.dataset.g5GurumiRehostReady = '1';
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitBtn || undefined);
          } else if (submitBtn) {
            submitBtn.click();
          } else {
            HTMLFormElement.prototype.submit.call(form);
          }
        })();
      },
      true,
    );
  }

  bindFormSubmitRehost();
  self._rehostBeforeSubmit = rehostExternalImagesBeforeSubmit;

  async function insertLinkPreview(urlOrCard) {
    if (typeof urlOrCard === 'string') {
      try {
        const preview = await fetchLinkPreview(urlOrCard, config);
        editor
          .chain()
          .focus()
          .insertLinkPreview({
            href: preview.url,
            title: preview.title,
            description: preview.description,
            image: preview.image,
            domain: preview.domain,
            alignment: 'center',
          })
          .run();
      } catch {
        editor
          .chain()
          .focus()
          .insertContent(
            `<p><a href="${urlOrCard}" target="_blank" rel="noopener noreferrer">${urlOrCard}</a></p>`,
          )
          .run();
      }
      return;
    }
    editor.chain().focus().insertLinkPreview({ ...urlOrCard, alignment: 'center' }).run();
  }

  function setAlignment(alignment) {
    if (editor.isActive('image')) {
      editor.chain().focus().updateAttributes('image', { alignment }).run();
      return;
    }
    if (editor.isActive('youtube')) {
      editor.chain().focus().updateAttributes('youtube', { alignment }).run();
      return;
    }
    if (editor.isActive('linkPreview')) {
      editor.chain().focus().updateAttributes('linkPreview', { alignment }).run();
      return;
    }
    editor.chain().focus().setTextAlign(alignment).run();
  }

  const handlers = {
    openLink() {
      const cardMode = editor.state.selection.empty;
      openLinkPreviewModal({
        initialUrl: String(editor.getAttributes('link').href ?? ''),
        cardMode: mode !== 'comment' && cardMode,
        config,
        onInsertTextLink(href) {
          editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
        },
        onInsertPreview: async (card) => insertLinkPreview(card),
      });
    },
    openImage() {
      fileInput.click();
    },
    openYoutube() {
      setError(null);
      openInputModal({
        title: 'YouTube 동영상',
        description:
          mode === 'comment'
            ? '댓글에 삽입할 YouTube 주소를 입력하세요.'
            : '공유 가능한 YouTube 주소를 붙여넣으세요.',
        label: 'YouTube URL',
        placeholder: 'https://www.youtube.com/watch?v=...',
        confirmLabel: '추가',
        onConfirm(url) {
          // [YouTube 삽입]
          // 역할: NodeSelection 해제 후 삽입 — 실패 시 모달 유지
          // 작성/수정일: 2026-07-29
          const trimmed = String(url || '').trim();
          if (!isYoutubeUrl(trimmed)) {
            alert('YouTube URL을 입력해 주세요.');
            return false;
          }

          let chain = editor.chain().focus();
          const selection = editor.state.selection;
          if (selection && selection.node) {
            chain = chain.setTextSelection(selection.to);
          }

          const inserted = chain
            .insertContent({
              type: 'youtube',
              attrs: {
                src: trimmed,
                width: 640,
                height: 360,
                alignment: 'center',
              },
            })
            .run();

          if (!inserted) {
            alert('영상을 본문에 삽입하지 못했습니다. 커서를 본문 텍스트에 두고 다시 시도해 주세요.');
            return false;
          }

          // 삽입 직후 노드 선택 잔여 핸들 제거
          try {
            const end = editor.state.doc.content.size;
            const pos = Math.min(editor.state.selection.to, end);
            editor.chain().setTextSelection(Math.max(1, pos)).run();
            editor.commands.blur();
          } catch (_) {
            /* ignore */
          }
          setError(null);
          return true;
        },
      });
    },
    openAlt() {
      if (!editor.isActive('image')) return;
      openInputModal({
        title: '대체텍스트',
        description: '이미지를 설명하는 짧은 문구를 입력하세요.',
        label: '대체텍스트',
        placeholder: '예: 차트 캡처 화면',
        confirmLabel: '저장',
        inputType: 'text',
        initialValue: String(editor.getAttributes('image').alt ?? ''),
        onConfirm(alt) {
          editor.chain().focus().updateAttributes('image', { alt }).run();
          // [대체텍스트] 역할: 저장 후 노드 선택 하이라이트 해제 작성/수정일: 2026-07-29
          const { to } = editor.state.selection;
          editor.chain().setTextSelection(to).blur().run();
        },
      });
    },
    setAlignment,
  };

  // [툴바 프리셋] features로 버튼 노출 제어 — 작성/수정일: 2026-08-12
  this._toolbarApi = createToolbar(editor, handlers, mode, { onStateChange: refreshToolbar }, features);
  // [간소화 댓글] 역할: 위젯에서 미디어 모달/파일창 호출 — 작성/수정일: 2026-07-29
  this._handlers = handlers;
  this.openImage = function () {
    handlers.openImage();
  };
  this.openYoutube = function () {
    handlers.openYoutube();
  };

  async function handleImage(file) {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 선택할 수 있습니다.');
      return;
    }
    if (file.size > config.maxUploadBytes) {
      setError('이미지는 10MB 이하만 업로드할 수 있습니다.');
      return;
    }

    let blobUrl = null;
    let placeholderPos = null;
    setUploading(true);
    setError(null);

    try {
      const { width: nw, height: nh } = await loadImageFileDimensions(file);
      // [이미지 폭] 최초 삽입 크기도 게시판·에디터 상한 적용 — 작성/수정일: 2026-08-09
      const size = computeEditorImageDisplaySize(nw, nh, getEditorMediaMaxWidth(editor));
      blobUrl = URL.createObjectURL(file);
      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'image',
            attrs: {
              src: blobUrl,
              alt: '',
              width: size.width,
              height: size.height,
              uploading: true,
              alignment: 'center',
            },
          },
          { type: 'paragraph' },
        ])
        .run();

      placeholderPos = findEditorImagePosBySrc(editor, blobUrl);
      const data = await uploadImage(file, config);
      const finalWidth = Math.min(data.width || size.width, size.width);
      const pos = placeholderPos ?? findEditorImagePosBySrc(editor, blobUrl);
      if (pos == null) throw new Error('업로드한 이미지 위치를 찾지 못했습니다.');

      replaceEditorImageAtPos(editor, pos, {
        src: data.url,
        alt: data.alt || '',
        width: finalWidth,
        height: Math.max(
          1,
          Math.round((data.height || size.height) * (finalWidth / Math.max(data.width || size.width, 1))),
        ),
        uploading: false,
        alignment: 'center',
      });
      self.syncToTextarea();
    } catch (err) {
      if (blobUrl) {
        const pos = placeholderPos ?? findEditorImagePosBySrc(editor, blobUrl);
        if (pos != null) {
          const node = editor.state.doc.nodeAt(pos);
          const size = node ? node.nodeSize : 1;
          editor.chain().focus().deleteRange({ from: pos, to: pos + size }).run();
        }
      }
      setError(err.message || '이미지 업로드에 실패했습니다.');
    } finally {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setUploading(false);
    }
  }

  async function handleImages(files) {
    for (const file of files) await handleImage(file);
    fileInput.value = '';
  }

  fileInput.addEventListener('change', () => {
    const files = Array.from(fileInput.files || []);
    if (files.length) void handleImages(files);
  });

  container.appendChild(this._toolbarApi.bar);
  container.appendChild(errorEl);
  container.appendChild(editorEl);

  refreshToolbar();
  this.syncToTextarea();
};

G5GurumiEditor.prototype.getContent = function () {
  if (!this._editor) return this.elPlaceHolder ? this.elPlaceHolder.value : '';
  try {
    return this._editor.getHTML();
  } catch (err) {
    // [Gurumi 에디터] renderHTML 오류 시 textarea 기존값 유지 — 작성/수정일: 2026-07-29
    console.error('[g5-gurumi] getHTML failed', err);
    return this.elPlaceHolder ? this.elPlaceHolder.value : '';
  }
};

G5GurumiEditor.prototype.getIR = function () {
  return this.getContent();
};

G5GurumiEditor.prototype.syncToTextarea = function () {
  if (!this.elPlaceHolder) return;
  this.elPlaceHolder.value = this.getContent();
};

G5GurumiEditor.prototype.setContent = function (html) {
  const content = html && String(html).trim() ? html : '<p></p>';
  if (this._editor) {
    this._editor.commands.setContent(content, { emitUpdate: false });
  }
  this.syncToTextarea();
};

G5GurumiEditor.prototype.exec = function (command, args) {
  args = args || [];
  switch (command) {
    case 'UPDATE_CONTENTS_FIELD':
      this.syncToTextarea();
      break;
    case 'FOCUS':
      if (this._editor) this._editor.commands.focus();
      else if (this.elPlaceHolder) this.elPlaceHolder.focus();
      break;
    case 'SET_CONTENTS':
      this.setContent(args[0] || '');
      break;
    case 'GET_IR':
      return this.getIR();
    default:
      break;
  }
};

G5GurumiEditor.prototype.destroy = function () {
  if (this._editor) {
    this._editor.destroy();
    this._editor = null;
  }
  if (this._wrap) {
    this._wrap.remove();
    this._wrap = null;
  }
  if (this.elPlaceHolder) {
    this.elPlaceHolder.style.display = '';
  }
};

window.G5GurumiEditor = G5GurumiEditor;

/** 댓글 등 프로그램 초기화용 헬퍼 */
window.G5Gurumi = {
  isEmpty: isEmptyHtml,
  createCommentEditor(fieldId, options) {
    const ta = document.getElementById(fieldId);
    if (!ta) return null;
    ta.classList.add('g5-gurumi');
    if (typeof oEditors === 'undefined') window.oEditors = [];
    if (!oEditors.getById) oEditors.getById = {};
    if (oEditors.getById[fieldId]) return oEditors.getById[fieldId];

    const opts = typeof g5_gurumi_opts !== 'undefined' ? g5_gurumi_opts : {};
    const editor = new G5GurumiEditor(fieldId, {
      editorUrl: options.editorUrl || (typeof g5_editor_url !== 'undefined' ? g5_editor_url : opts.pluginUrl || ''),
      uploadNonce: options.uploadNonce || options.nonce || (typeof ed_nonce !== 'undefined' ? ed_nonce : ''),
      mode: 'comment',
      preset: 'comment',
      features: options.features || {},
      boTable: options.boTable || opts.boTable || '',
      uploadMode: options.uploadMode || opts.uploadMode || 'board',
      maxUploadBytes: options.maxUploadBytes || opts.maxUploadBytes || 10 * 1024 * 1024,
      imageMaxWidth: options.imageMaxWidth || opts.imageMaxWidth || 0,
      endpoints: options.endpoints || opts.endpoints || {},
    });
    oEditors.push(editor);
    oEditors.getById[fieldId] = editor;
    // [호환] 레거시 window.ed_{fieldId} — 작성/수정일: 2026-08-12
    try { window['ed_' + fieldId] = editor; } catch (e) { /* ignore */ }
    return editor;
  },
  syncField(fieldId) {
    const ed = typeof oEditors !== 'undefined' && oEditors.getById ? oEditors.getById[fieldId] : null;
    if (ed) ed.exec('UPDATE_CONTENTS_FIELD', []);
  },
  setFieldContent(fieldId, html) {
    const ed = typeof oEditors !== 'undefined' && oEditors.getById ? oEditors.getById[fieldId] : null;
    if (ed) ed.setContent(html);
  },
  openImage(fieldId) {
    const ed = typeof oEditors !== 'undefined' && oEditors.getById ? oEditors.getById[fieldId] : null;
    if (ed && typeof ed.openImage === 'function') ed.openImage();
  },
  openYoutube(fieldId) {
    const ed = typeof oEditors !== 'undefined' && oEditors.getById ? oEditors.getById[fieldId] : null;
    if (ed && typeof ed.openYoutube === 'function') ed.openYoutube();
  },
  getContent(fieldId) {
    const ed = typeof oEditors !== 'undefined' && oEditors.getById ? oEditors.getById[fieldId] : null;
    return ed && typeof ed.getContent === 'function' ? ed.getContent() : '';
  },
};

export default G5GurumiEditor;
